import { Anchor, Badge, Breadcrumbs, Button, Group, Text, Tooltip } from '@mantine/core';
import { Suspense, useCallback, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';

import { useDataSourcesModal, getDatasets } from '@mygpt/app';
import { SvgAdd } from '@/components/Common/icon';
import { ProBadge } from '@/components/Common/ProBadge';
import { DEFAULT_MODEL_NAME } from '@/components/Create_V2/ModelSelect';
import { CloningTip } from '@/components/Datasets/CloningTip';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import FallBack from '@/components/SuspenseFallBack';
import { DatasetSourcesRoutes } from '@/routes';
import { useCreateStore } from '@/store/create';
import { useDatasetStore } from '@/store/dataset';

import { SvgArrow } from './icon';
import { useMediaQuery } from '@mantine/hooks';

interface SourcesListChildren extends SourcesListParams {
  url: string;
}
export interface SourcesListParams {
  type: string;
  title: string;
  icon: React.FC;
  activeIcon: React.FC;
  counts: number;
  children?: SourcesListChildren[];
  showProBadge?: boolean;
}

export default ({ SourcesTypeList }: { SourcesTypeList: SourcesListParams[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const matches = useMediaQuery('(max-height: 600px)');
  const { onOpen } = useDataSourcesModal();
  const { '*': type, datasetId } = useParams();
  const activeType = type?.split('/')[0];
  const { pathname } = useLocation();

  const { setDatasetModelName } = useCreateStore();
  const { datasetInfo, isCloning, setCloningTipOpened, setDatasetInfo, setIsCloning } = useDatasetStore();

  const initInfo = useCallback(() => {
    getDatasets({ id: datasetId }).then((res) => {
      setDatasetInfo(res.data);
      setIsCloning(['cloning_target', 'cloning_source'].includes(res.data.data_status));
      setDatasetModelName(res.data.metadata.embedding_model_name || DEFAULT_MODEL_NAME);
    });
  }, [datasetId]);

  useLayoutEffect(() => {
    initInfo();
  }, []);

  useEffect(() => {
    initInfo();
  }, [datasetId]);

  const handleAddDatasource = () => {
    if (isCloning) {
      setCloningTipOpened(true);
    } else {
      onOpen();
    }
  };

  const items = [{ title: t('page.data_set_title'), href: '/datasets' }, { title: datasetInfo?.name }].map(
    (item, index) => (
      <Anchor
        key={index}
        size={'xl'}
        c="gray"
        className={`${!item.href && 'cursor-default'} ${item.href ? 'hover:underline' : 'hover:no-underline'}`}
        onClick={() => {
          if (item.href) navigate(item.href, { replace: true });
        }}
      >
        <Tooltip label={item.title}>
          <Text fz={matches ? 25 : 34} fw={500} c={!index ? 'gray.8' : 'gray.5'} maw={'40vw'} truncate>
            {item.title}
          </Text>
        </Tooltip>
      </Anchor>
    )
  );

  return (
    <section className="flex flex-col">
      <section className="flex-none">
        <ContainerHeader
          title={<Breadcrumbs separator={<SvgArrow />}>{items}</Breadcrumbs>}
          description={t('dataset.detail_desc')}
          rightSection={
            <Button leftSection={<SvgAdd />} onClick={handleAddDatasource}>
              {t('dataset.add_datasource_btn')}
            </Button>
          }
        />
      </section>

      <Group className="relative mb-5">
        {SourcesTypeList.map((item, index) => {
          const isAtive = item.type === activeType;

          const ItemIcon = item.icon;

          return (
            <section
              key={index}
              className={`group relative rounded-xl text-gray-700 ${
                isAtive ? 'cursor-default bg-violet-100' : 'cursor-pointer hover:bg-violet-100'
              }`}
            >
              <Group
                className={`px-3 py-2`}
                justify="space-between"
                gap={30}
                wrap="nowrap"
                onClick={() => {
                  const path = pathname.split('/data-source')[0] + '/data-source/';
                  navigate(`${path}${item.type}`);
                }}
              >
                <Group gap={5} wrap="nowrap">
                  <section className="h-[16px] w-[16px] flex items-center justify-center">
                    <div className="scale-50">
                      <ItemIcon />
                    </div>
                  </section>
                  <Text size="sm">{item.title}</Text>
                  {item.showProBadge && <ProBadge />}
                  {item.counts && (
                    <Badge
                      size="sm"
                      color="violet.5"
                      variant="filled"
                      className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2"
                    >
                      {item.counts}
                    </Badge>
                  )}
                </Group>
              </Group>
            </section>
          );
        })}
      </Group>

      <section className="flex-auto">
        <Suspense fallback={<FallBack />}>
          <DatasetSourcesRoutes />
        </Suspense>
      </section>
      <CloningTip />
    </section>
  );
};
